// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import { formatLocale, Locale } from './locales';
import { RowErrors } from './rowErrors';
import { describeListSaveError } from './serverErrors';
import { ApiError, SaveFailure } from './serverErrors';

/**
 * Rows of a versioned per-arc list: values for each source and target locale pair. A null locale
 * is one not yet chosen, which fails validation; a stored null is shown as Any. A spec names the
 * value columns; `arcRows` builds the rows from the loaded items, checks them, tracks their
 * changes and turns them back into items. Used by the base rates and TER group pages. In the
 * types below, T is the loaded item, such as a base price, and V the keys of its value columns,
 * such as 'amount' | 'currency'.
 */

export interface LanguagePair {
  sourceLocale: string | null;
  targetLocale: string | null;
}

export type LocaleField = keyof LanguagePair;

export const LOCALE_FIELDS: LocaleField[] = ['sourceLocale', 'targetLocale'];

const LOCALE_LABELS: Record<LocaleField, string> = {
  sourceLocale: 'Source locale',
  targetLocale: 'Target locale',
};

const LOCALE_REQUIRED: Record<LocaleField, string> = {
  sourceLocale: 'Choose a source locale.',
  targetLocale: 'Choose a target locale.',
};

/** A value column, edited as text or from `options`, and uploaded as text. */
export interface ArcColumn<V extends string> {
  key: V;
  header: string;
  // The error for an entered value, if any.
  validate: (value: string) => string | undefined;
  // Whether an entered value still equals the loaded one; exact text by default.
  same?: (entered: string, loaded: string) => boolean;
  // Applied to typed and uploaded values, e.g. upper-casing a code.
  normalize?: (value: string) => string;
  // Edited with a number input, in steps of `step` when given.
  numeric?: boolean;
  step?: number;
  // Edited with a dropdown of these values instead of an input.
  options?: string[];
  placeholder?: string;
}

/** An entered number equals the loaded one when both are the same number, however written. */
export const sameNumber = (entered: string, loaded: string): boolean =>
  entered.trim() !== '' && Number(entered) === Number(loaded);

export type ArcField<V extends string> = LocaleField | V;

export type ArcRow<V extends string> = LanguagePair & Record<V, string> & { key: number };

/** A row of the edit table, compared against the loaded item it started from. */
export type ArcEditRow<T, V extends string> = ArcRow<V> & {
  // Null for rows added during this edit.
  original: T | null;
};

export interface ArcRowsSpec<T extends { languagePair: LanguagePair }, V extends string> {
  columns: ArcColumn<V>[];
  valuesOf: (item: T) => Record<V, string>;
  itemOf: (languagePair: LanguagePair, values: Record<V, string>) => T;
  // Request property holding the items, e.g. "basePrices"; save errors are placed by its paths.
  collection: string;
  // Shown for a save error the server sent without a message.
  rejectedMessage: string;
  accessDeniedMessage: string;
}

export interface ArcRows<T, V extends string> {
  columns: ArcColumn<V>[];
  // The locale fields, then the value columns; also the columns an upload must name.
  fields: ArcField<V>[];
  fieldLabels: Record<ArcField<V>, string>;
  toEditRow: (item?: T) => ArcEditRow<T, V>;
  // Builds a row from the cells of an uploaded row; an empty locale is null, which fails validation.
  toUploadRow: (record: Record<ArcField<V>, string>) => ArcRow<V>;
  toItem: (row: ArcRow<V>) => T;
  // The locale check is skipped while the locales are unknown.
  validateRows: (rows: ArcRow<V>[], locales: Locale[] | null) => RowErrors<ArcField<V>>[];
  // Fields of a loaded row that differ from the loaded item; empty for added rows.
  changedFields: (row: ArcEditRow<T, V>) => ArcField<V>[];
  // Whether the edit rows differ from the `loadedCount` items they started from.
  hasChanges: (rows: ArcEditRow<T, V>[], loadedCount: number) => boolean;
  // Places a failed save's errors on the `rowCount` submitted rows; a 409 is a version conflict.
  describeSaveError: (error: ApiError, rowCount: number) => SaveFailure<ArcField<V>>;
}

// "Any" (null) only matches another "Any", never a specific locale.
export const arcKey = (pair: LanguagePair): string =>
  JSON.stringify([pair.sourceLocale, pair.targetLocale]);

export type ArcChangeKind = 'added' | 'removed' | 'changed';

/** An arc whose item differs between two versions of a per-arc list; null where it is absent. */
export interface ArcChange<T> {
  kind: ArcChangeKind;
  languagePair: LanguagePair;
  before: T | null;
  after: T | null;
}

export interface ArcComparison<T> {
  changes: ArcChange<T>[];
  // Items of `after` equal to those they replace.
  unchanged: T[];
}

/**
 * Compares a per-arc list with the one it replaces, matching items by arc. Changes keep the order
 * of `after`, then list the removed arcs in the order of `before`.
 */
export function compareArcs<T extends { languagePair: LanguagePair }>(
  before: T[],
  after: T[],
  same: (beforeItem: T, afterItem: T) => boolean
): ArcComparison<T> {
  const beforeByArc = new Map(before.map(item => [arcKey(item.languagePair), item]));
  const afterArcs = new Set(after.map(item => arcKey(item.languagePair)));
  const changes: ArcChange<T>[] = [];
  const unchanged: T[] = [];
  after.forEach(item => {
    const replaced = beforeByArc.get(arcKey(item.languagePair));
    if (!replaced) {
      changes.push({ kind: 'added', languagePair: item.languagePair, before: null, after: item });
    } else if (same(replaced, item)) {
      unchanged.push(item);
    } else {
      changes.push({
        kind: 'changed',
        languagePair: item.languagePair,
        before: replaced,
        after: item,
      });
    }
  });
  before
    .filter(item => !afterArcs.has(arcKey(item.languagePair)))
    .forEach(item =>
      changes.push({ kind: 'removed', languagePair: item.languagePair, before: item, after: null })
    );
  return { changes, unchanged };
}

/** The filter box placeholder of a table of arcs filtered with `describeArc`. */
export const ARC_FILTERING_PLACEHOLDER = 'Find arcs';

/** Orders arcs by source locale code, then target; Any (null) comes first. */
export const byArc = (a: LanguagePair, b: LanguagePair): number =>
  (a.sourceLocale || '').localeCompare(b.sourceLocale || '') ||
  (a.targetLocale || '').localeCompare(b.targetLocale || '');

/** An arc as its locale codes, e.g. "en-US → de-DE"; a null locale reads Any. */
export const describeArc = (pair: LanguagePair): string =>
  `${formatLocale(pair.sourceLocale)} → ${formatLocale(pair.targetLocale)}`;

const hasBothLocales = (row: LanguagePair): boolean =>
  row.sourceLocale !== null && row.targetLocale !== null;

/**
 * Flags missing locales, locales missing from `locales` (unless that is null), and rows that share
 * an arc. A row missing a locale is not checked for duplicates: choosing one is the fix.
 */
const validateArcs = (rows: LanguagePair[], locales: Locale[] | null): RowErrors<LocaleField>[] => {
  const localeCodes = locales && new Set(locales.map(locale => locale.code));
  const rowCountByArc = new Map<string, number>();
  rows.filter(hasBothLocales).forEach(row => {
    const key = arcKey(row);
    rowCountByArc.set(key, (rowCountByArc.get(key) || 0) + 1);
  });

  return rows.map(row => {
    const errors: RowErrors<LocaleField> = {};
    LOCALE_FIELDS.forEach(field => {
      const code = row[field];
      if (code === null) {
        errors[field] = LOCALE_REQUIRED[field];
      } else if (localeCodes && !localeCodes.has(code)) {
        errors[field] = `Unsupported locale ${code}`;
      }
    });
    if (hasBothLocales(row) && (rowCountByArc.get(arcKey(row)) || 0) > 1) {
      errors.row = `Duplicate arc ${describeArc(row)}`;
    }
    return errors;
  });
};

let nextRowKey = 0;

export function arcRows<T extends { languagePair: LanguagePair }, V extends string>(
  spec: ArcRowsSpec<T, V>
): ArcRows<T, V> {
  const { columns, valuesOf, itemOf } = spec;
  const fields: ArcField<V>[] = [...LOCALE_FIELDS, ...columns.map(column => column.key)];
  const fieldLabels = { ...LOCALE_LABELS } as Record<ArcField<V>, string>;
  columns.forEach(column => {
    fieldLabels[column.key] = column.header;
  });

  const toRow = (pair: LanguagePair, values: Record<V, string>): ArcRow<V> => {
    nextRowKey += 1;
    return {
      key: nextRowKey,
      sourceLocale: pair.sourceLocale,
      targetLocale: pair.targetLocale,
      ...values,
    } as ArcRow<V>;
  };

  const emptyValues = (): Record<V, string> => {
    const values = {} as Record<V, string>;
    columns.forEach(column => {
      values[column.key] = '';
    });
    return values;
  };

  const toEditRow = (item?: T): ArcEditRow<T, V> => {
    const row = item
      ? toRow(item.languagePair, valuesOf(item))
      : toRow({ sourceLocale: null, targetLocale: null }, emptyValues());
    return { ...row, original: item || null };
  };

  const toUploadRow = (record: Record<ArcField<V>, string>): ArcRow<V> => {
    const values = {} as Record<V, string>;
    columns.forEach(({ key, normalize }) => {
      values[key] = normalize ? normalize(record[key]) : record[key];
    });
    return toRow(
      { sourceLocale: record.sourceLocale || null, targetLocale: record.targetLocale || null },
      values
    );
  };

  const toItem = (row: ArcRow<V>): T => {
    const values = {} as Record<V, string>;
    columns.forEach(({ key }) => {
      values[key] = row[key];
    });
    return itemOf({ sourceLocale: row.sourceLocale, targetLocale: row.targetLocale }, values);
  };

  const validateRows = (rows: ArcRow<V>[], locales: Locale[] | null): RowErrors<ArcField<V>>[] =>
    validateArcs(rows, locales).map((arcErrors, index) => {
      const errors: RowErrors<ArcField<V>> = { ...arcErrors };
      columns.forEach(({ key, validate }) => {
        const error = validate(rows[index][key]);
        if (error) {
          errors[key] = error;
        }
      });
      return errors;
    });

  const changedFields = (row: ArcEditRow<T, V>): ArcField<V>[] => {
    const { original } = row;
    if (!original) {
      return [];
    }
    const loaded = valuesOf(original);
    const changedLocales = LOCALE_FIELDS.filter(
      field => row[field] !== original.languagePair[field]
    );
    const changedValues = columns
      .filter(({ key, same }) => !(same ? same(row[key], loaded[key]) : row[key] === loaded[key]))
      .map(({ key }) => key);
    return [...changedLocales, ...changedValues];
  };

  return {
    columns,
    fields,
    fieldLabels,
    toEditRow,
    toUploadRow,
    toItem,
    validateRows,
    changedFields,
    hasChanges: (rows, loadedCount): boolean =>
      rows.length !== loadedCount ||
      rows.some(row => !row.original || changedFields(row).length > 0),
    describeSaveError: (error, rowCount): SaveFailure<ArcField<V>> =>
      describeListSaveError(error, {
        collection: spec.collection,
        rowCount,
        fields,
        rejectedMessage: spec.rejectedMessage,
        accessDeniedMessage: spec.accessDeniedMessage,
      }),
  };
}
